import { useEffect, useState } from 'react';
import { Pencil, RotateCcw, Trash2, X } from 'lucide-react';
import { useAuthedApi } from '../../lib/useAuthedApi';
import { SlugField, resolvedSlug, type SlugValue } from '../../components/SlugField';
import {
  type MeetingType,
  MeetingModePicker,
  MeetingTypeBadge,
  offeredModes,
} from '../../lib/meetingType';
import {
  type PreviewMeetingType,
  MeetingTypeSeg,
  EmailMessagePreview,
  SmsMessagePreview,
  defaultReminderEmailBody,
  defaultReminderSmsText,
} from '../../lib/messagePreview';

// Convert a modes list <-> the API's offers_* flags.
function offersBody(modes: MeetingType[]) {
  return {
    offers_in_person: modes.includes('in_person'),
    offers_phone: modes.includes('phone_call'),
    offers_video: modes.includes('video_call'),
  };
}

// Per-service reminder copy applies to every reminder rule for the service,
// which can fire at different lead times — so the preview shows a representative
// day-before reminder. The default-wording lead-in phrase ("tomorrow") varies
// with the actual rule timing (configured under Settings → Reminders).
const REMINDER_PREVIEW_OFFSET_MIN = 1440;

type Service = {
  id: string;
  name: string;
  slug: string;
  duration_minutes: number;
  offers_in_person: number;
  offers_phone: number;
  offers_video: number;
  description: string | null;
  reminder_email_subject: string | null;
  reminder_email_body: string | null;
  reminder_sms_body: string | null;
  is_active: number;
};

// Numeric fields are held as strings while editing so the input can be cleared
// (an empty box) instead of snapping back to a stuck "0"; parsed on save.
type Editing = {
  id: string;
  name: string;
  duration_minutes: string;
  modes: MeetingType[];
  description: string;
  reminder_email_subject: string;
  reminder_email_body: string;
  reminder_sms_body: string;
} | null;

export default function Services() {
  const apiFn = useAuthedApi();
  const [items, setItems] = useState<Service[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    duration_minutes: '30',
    modes: ['in_person', 'video_call', 'phone_call'] as MeetingType[],
  });
  const [slug, setSlug] = useState<SlugValue>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  // Brand info for the reminder preview shell (logo + signature).
  const [brand, setBrand] = useState<{ name: string; logoUrl: string | null }>({
    name: '',
    logoUrl: null,
  });
  const [previewType, setPreviewType] = useState<PreviewMeetingType>('in_person');

  const load = () =>
    apiFn<Service[]>('/services')
      .then(setItems)
      .catch((e) => setErr(e.message));

  useEffect(() => {
    void load();
    apiFn<{ name: string; brand_name: string | null; brand_logo_url: string | null }>(
      '/organizations/me',
    )
      .then((o) => setBrand({ name: o.brand_name || o.name, logoUrl: o.brand_logo_url }))
      .catch(() => {
        /* preview shell falls back to a generic brand name */
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function create() {
    const duration = parseInt(form.duration_minutes, 10);
    if (!form.name.trim() || Number.isNaN(duration) || duration < 5) {
      setErr('Name and a duration of at least 5 minutes are required.');
      return;
    }
    if (form.modes.length === 0) {
      setErr('Pick at least one meeting mode.');
      return;
    }
    setCreating(true);
    setErr(null);
    try {
      await apiFn<Service>('/services', {
        body: {
          name: form.name,
          duration_minutes: duration,
          ...offersBody(form.modes),
          slug: resolvedSlug(slug, form.name),
        },
      });
      setForm({
        name: '',
        duration_minutes: '30',
        modes: ['in_person', 'video_call', 'phone_call'],
      });
      setSlug(null);
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setCreating(false);
    }
  }

  function startEdit(s: Service) {
    setErr(null);
    setEditing({
      id: s.id,
      name: s.name,
      duration_minutes: String(s.duration_minutes),
      modes: offeredModes(s),
      description: s.description ?? '',
      reminder_email_subject: s.reminder_email_subject ?? '',
      reminder_email_body: s.reminder_email_body ?? '',
      reminder_sms_body: s.reminder_sms_body ?? '',
    });
  }

  function cancelEdit() {
    setEditing(null);
  }

  async function saveEdit() {
    if (!editing) return;
    const duration = parseInt(editing.duration_minutes, 10);
    if (!editing.name.trim()) {
      setErr('Name is required.');
      return;
    }
    if (Number.isNaN(duration) || duration < 5) {
      setErr('Duration must be at least 5 minutes.');
      return;
    }
    if (editing.modes.length === 0) {
      setErr('Pick at least one meeting mode.');
      return;
    }
    setSavingEdit(true);
    setErr(null);
    try {
      await apiFn<Service>(`/services/${editing.id}`, {
        method: 'PATCH',
        body: {
          name: editing.name.trim(),
          duration_minutes: duration,
          ...offersBody(editing.modes),
          description: editing.description.trim() || null,
          reminder_email_subject: editing.reminder_email_subject.trim() || null,
          reminder_email_body: editing.reminder_email_body.trim() || null,
          reminder_sms_body: editing.reminder_sms_body.trim() || null,
        },
      });
      setEditing(null);
      await load();
    } catch (e) {
      // Server rejects copy with unknown {{placeholders}}; surface the bad one.
      const body = (e as { body?: { error?: string; token?: string } }).body;
      if (body?.error === 'unknown_token' && body.token) {
        setErr(`Unknown placeholder: {{${body.token}}}. Check the supported placeholders.`);
      } else {
        setErr((e as Error).message);
      }
    } finally {
      setSavingEdit(false);
    }
  }

  async function deactivate(id: string, name: string) {
    if (!confirm(`Deactivate ${name}? It'll stop appearing in the booking flow.`)) return;
    try {
      await apiFn(`/services/${id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  async function reactivate(id: string) {
    setErr(null);
    try {
      await apiFn(`/services/${id}`, { method: 'PATCH', body: { is_active: true } });
      await load();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  const activeItems = items.filter((s) => s.is_active === 1);
  const inactiveItems = items.filter((s) => s.is_active !== 1);

  return (
    <div>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Add a service</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          The things you let customers book &mdash; e.g. &ldquo;Coverage
          review&rdquo;, &ldquo;Strategy call&rdquo;, &ldquo;30-min
          consultation.&rdquo;
        </p>

        <label>
          <span>Name</span>
          <input
            placeholder="30-min consultation"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </label>

        <label>
          <span>URL slug</span>
          <SlugField
            derivedFromName={form.name}
            value={slug}
            onChange={setSlug}
            prefix="/services/"
            placeholder="30-min-consultation"
          />
          <p className="field-help">
            Auto-generated from the name. Click the pencil to customize.
          </p>
        </label>

        <div className="grid-2">
          <label>
            <span>Duration (minutes)</span>
            <input
              type="number"
              min={5}
              max={480}
              value={form.duration_minutes}
              placeholder="30"
              onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
            />
          </label>
          <label>
            <span>Meeting modes offered</span>
            <MeetingModePicker
              value={form.modes}
              onChange={(modes) => setForm({ ...form, modes })}
            />
          </label>
        </div>

        {err && !editing && <p className="err">{err}</p>}
        <button
          className="primary"
          onClick={create}
          disabled={creating || form.modes.length === 0}
        >
          {creating ? 'Adding…' : 'Add service'}
        </button>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>
          Your services{' '}
          {activeItems.length > 0 && <span className="muted small">({activeItems.length})</span>}
        </h3>

        {items.length === 0 ? (
          <p className="muted">No services yet. Add one above to get started.</p>
        ) : activeItems.length === 0 ? (
          <p className="muted">
            No active services. {inactiveItems.length > 0 && 'See inactive services below,'} add
            one above to get started.
          </p>
        ) : (
          <table className="bare">
            <tbody>
              {activeItems.map((s) => {
                const isEditingThis = editing?.id === s.id;
                if (isEditingThis && editing) {
                  return (
                    <tr key={s.id}>
                      <td colSpan={4} style={{ padding: '1rem' }}>
                        <div className="row" style={{ marginBottom: '0.5rem' }}>
                          <strong>Edit service</strong>
                          <span className="spacer" />
                          <button className="ghost" onClick={cancelEdit} aria-label="Cancel edit">
                            <X size={16} />
                          </button>
                        </div>

                        <label>
                          <span>Name</span>
                          <input
                            value={editing.name}
                            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                          />
                        </label>

                        <div className="grid-2">
                          <label>
                            <span>Duration (minutes)</span>
                            <input
                              type="number"
                              min={5}
                              max={480}
                              value={editing.duration_minutes}
                              placeholder="30"
                              onChange={(e) =>
                                setEditing({ ...editing, duration_minutes: e.target.value })
                              }
                            />
                          </label>
                          <label>
                            <span>Meeting modes offered</span>
                            <MeetingModePicker
                              value={editing.modes}
                              onChange={(modes) => setEditing({ ...editing, modes })}
                            />
                          </label>
                        </div>

                        <label>
                          <span>Description (optional)</span>
                          <textarea
                            rows={3}
                            value={editing.description}
                            onChange={(e) =>
                              setEditing({ ...editing, description: e.target.value })
                            }
                            placeholder="A sentence or two shown to bookees on the service picker."
                          />
                        </label>

                        <details className="dev-settings" style={{ marginTop: '0.5rem' }}>
                          <summary>Reminder messages for this service</summary>
                          <div className="dev-settings-body">
                            <p className="muted" style={{ marginTop: 0 }}>
                              Override reminder copy for this service &mdash; parking
                              for a clinic visit, a video link for a call. Blank uses
                              your defaults. (Timing &amp; channels live under{' '}
                              <strong>Settings &rarr; Reminders</strong>.)
                            </p>

                            <label>
                              <span>Reminder email subject</span>
                              <input
                                value={editing.reminder_email_subject}
                                onChange={(e) =>
                                  setEditing({ ...editing, reminder_email_subject: e.target.value })
                                }
                                placeholder="Your {{service_name}} is coming up"
                                maxLength={200}
                              />
                            </label>

                            <label>
                              <span>Reminder email message</span>
                              <textarea
                                rows={4}
                                value={editing.reminder_email_body}
                                onChange={(e) =>
                                  setEditing({ ...editing, reminder_email_body: e.target.value })
                                }
                                placeholder="Hi {{bookee_name}}, a reminder of your {{service_name}} on {{start_time}}. Please arrive 10 minutes early — parking is on Level 2."
                                maxLength={2000}
                                style={{ fontFamily: 'inherit' }}
                              />
                            </label>

                            <label>
                              <span>Reminder SMS message</span>
                              <textarea
                                rows={2}
                                value={editing.reminder_sms_body}
                                onChange={(e) =>
                                  setEditing({ ...editing, reminder_sms_body: e.target.value })
                                }
                                placeholder="Reminder: {{service_name}} on {{start_time}} at {{location_name}}."
                                maxLength={320}
                                style={{ fontFamily: 'inherit' }}
                              />
                            </label>

                            <p className="field-help">
                              Supports placeholders: <code>{`{{bookee_name}}`}</code>,{' '}
                              <code>{`{{start_time}}`}</code>, <code>{`{{location_name}}`}</code>,{' '}
                              <code>{`{{location_address}}`}</code>,{' '}
                              <code>{`{{staff_name}}`}</code>, <code>{`{{service_name}}`}</code>.
                            </p>

                            <MeetingTypeSeg value={previewType} onChange={setPreviewType} />
                            <p className="field-help" style={{ marginTop: 0 }}>
                              Preview shows a day-before reminder; the default
                              wording&rsquo;s lead-in (&ldquo;tomorrow&rdquo;) shifts
                              to match each reminder&rsquo;s actual timing.
                            </p>
                            <EmailMessagePreview
                              brandName={brand.name || 'Your brand'}
                              brandLogoUrl={brand.logoUrl}
                              body={editing.reminder_email_body}
                              previewType={previewType}
                              fallbackBody={defaultReminderEmailBody(
                                REMINDER_PREVIEW_OFFSET_MIN,
                                previewType,
                              )}
                            />
                            <SmsMessagePreview
                              text={editing.reminder_sms_body}
                              previewType={previewType}
                              fallbackText={defaultReminderSmsText(
                                REMINDER_PREVIEW_OFFSET_MIN,
                                brand.name || 'Your brand',
                              )}
                            />
                          </div>
                        </details>

                        {err && <p className="err">{err}</p>}
                        <div className="row" style={{ gap: '0.5rem' }}>
                          <button
                            className="primary"
                            onClick={saveEdit}
                            disabled={
                              savingEdit ||
                              !editing.name.trim() ||
                              (parseInt(editing.duration_minutes, 10) || 0) < 5 ||
                              editing.modes.length === 0
                            }
                          >
                            {savingEdit ? 'Saving…' : 'Save changes'}
                          </button>
                          <button className="ghost" onClick={cancelEdit}>
                            Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={s.id}>
                    <td>
                      <div>{s.name}</div>
                      {s.description && (
                        <div className="muted small" style={{ marginTop: '0.15rem' }}>
                          {s.description}
                        </div>
                      )}
                    </td>
                    <td className="muted small" style={{ whiteSpace: 'nowrap' }}>
                      {s.duration_minutes} min
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                        {offeredModes(s).map((m) => (
                          <MeetingTypeBadge key={m} type={m} />
                        ))}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        className="icon-btn"
                        onClick={() => startEdit(s)}
                        title="Edit"
                        aria-label={`Edit ${s.name}`}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        className="icon-btn icon-btn-danger"
                        onClick={() => deactivate(s.id, s.name)}
                        title="Deactivate"
                        aria-label={`Deactivate ${s.name}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {inactiveItems.length > 0 && (
        <details className="dev-settings">
          <summary>
            Inactive services <span className="muted small">({inactiveItems.length})</span>
          </summary>
          <div className="dev-settings-body">
            <p className="muted" style={{ marginTop: 0 }}>
              Deactivated services are hidden from the booking flow. Reactivate one
              to make it bookable again.
            </p>
            <table className="bare">
              <tbody>
                {inactiveItems.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <div>{s.name}</div>
                      {s.description && (
                        <div className="muted small" style={{ marginTop: '0.15rem' }}>
                          {s.description}
                        </div>
                      )}
                    </td>
                    <td className="muted small" style={{ whiteSpace: 'nowrap' }}>
                      {s.duration_minutes} min
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                        {offeredModes(s).map((m) => (
                          <MeetingTypeBadge key={m} type={m} />
                        ))}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="ghost" onClick={() => reactivate(s.id)}>
                        <RotateCcw size={14} /> Reactivate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}
